/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import {
	ISchemaConstEnumModel,
	TGenericModelNode,
	TSchemaConstEnumValueSchema
} from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { useTranslate } from "@adapptio/lib-components";
import { FieldDropdown, TFieldDropdownOption } from "../../Fields/FieldDropdown";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";
import { FieldButton, FieldGroup } from "@adapptio/lib-editor-ui";

export const ControlConstEnum = (model: TGenericModelNode): IInspectorControls => {
	if (model.schema.opts.editorOptions?.controlType === "buttons") {
		return {
			primary: {
				component: ControlConstEnum_PrimaryButtons,
				model: model
			}
		};
	} else {
		return {
			primary: {
				component: ControlConstEnum_Primary,
				model: model
			}
		};
	}
};

export const ControlConstEnum_Primary: TInspectorControlComponent<
	ISchemaConstEnumModel<TSchemaConstEnumValueSchema>
> = ({ model: _model, id }) => {
	const t = useTranslate();
	const editorInspectorCtx = useEditorInspectorContext();
	const { model, validationErrors } = useBlueprintModel(_model, "constEnum");

	const options = useMemo(() => {
		if (!model) {
			return [];
		}

		return model.schema.opts.options.map((item) => {
			if (item.separator) {
				return {
					isSeparator: true
				} as TFieldDropdownOption;
			} else {
				return {
					label: t("editor", item.label),
					value: String(item.value),
					description: item.description,
					icon: item.icon
				} as TFieldDropdownOption;
			}
		});
	}, [ model.schema ]);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldDropdown
			id={id}
			value={String(model.value)}
			options={options}
			onChange={handleChange}
			validationErrors={validationErrors}
			readOnly={editorInspectorCtx?.readOnly}
			grow={true}
		/>
	);
};

export const ControlConstEnum_PrimaryButtons: TInspectorControlComponent<
	ISchemaConstEnumModel<TSchemaConstEnumValueSchema>
> = ({ model: _model }) => {
	const t = useTranslate();
	const editorInspectorCtx = useEditorInspectorContext();
	const { model } = useBlueprintModel(_model, "constEnum");

	const handleChange = useCallback(
		(value: string | number) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const schema = model.schema;

	return (
		<FieldGroup>
			{schema.opts.options.map((option, index) => {
				return (
					<FieldButton
						key={index}
						icon={option.icon}
						tooltip={option.icon ? t("editor", option.label) : undefined}
						selected={model.value === option.value}
						onClick={() => handleChange(option.value)}
						readOnly={editorInspectorCtx?.readOnly}
					>
						{!option.icon ? t("editor", option.label) : null}
					</FieldButton>
				);
			})}
		</FieldGroup>
	);
};
